// The pan/zoom transform, as arithmetic.
//
// Extracted from Editor so "zoom to this element" can be tested without a DOM.
// The Editor's transform is `translate(tx ty) scale(k)` and nothing else (see the
// `<g>` in Editor's render), so metre space maps to viewport pixels by
// `px = m * k + t` — which is why a validator annotation given in metres lands on
// the drawing with no conversion, and why fitting a bbox is four lines of algebra
// rather than a matrix.

export interface View { k: number; tx: number; ty: number }

/** Zoom limits, matching the wheel handler in Editor. Kept here so the two
 *  cannot drift: a fitted view outside the wheel's clamp jumps on the user's
 *  first scroll. */
export const K_MIN = 4;
export const K_MAX = 60;

export type Bbox = [number, number, number, number];   // x0, y0, x1, y1 in metres

/** Pixels of the viewport hidden by the panels floating over it.
 *
 *  Both panels are absolutely positioned over the canvas, so a bbox can be
 *  "inside the viewport" by the arithmetic and still be completely covered by
 *  the drawer. Framing has to happen in the part of the window the user can
 *  actually see, or clicking a rule instance appears to do nothing. */
export interface Insets { left?: number; right?: number; top?: number; bottom?: number }

const box = (vw: number, vh: number, ins: Insets) => {
  const l = ins.left ?? 0, r = ins.right ?? 0, t = ins.top ?? 0, b = ins.bottom ?? 0;
  return { l, t, w: Math.max(vw - l - r, 80), h: Math.max(vh - t - b, 80) };
};

/** The view that centres `bbox` in the visible part of a `vw` x `vh` viewport.
 *
 *  `padM` is breathing room in METRES, not pixels, so a 0,80 m corridor stretch
 *  is not framed so tightly that the wall it is measured against falls outside
 *  the picture. The point of jumping to an element is to see it in context.
 *
 *  `maxK` caps how far a small element is allowed to fill the screen: fitting a
 *  0,80 x 2,00 m stretch to a 1200 px viewport would ask for 300 px/m, ten times
 *  past the zoom the plan is legible at, and land the user inside a blank white
 *  rectangle with no idea which flat they are in.
 *
 *  `minK` is the other end, and it is a FLOOR ON THE FIT, which is a strange
 *  thing for a fit to have: asked to frame something too big for the window, it
 *  returns a view that does not frame it. The default `K_MIN` is 4 px/m, so a
 *  322 m plot — an ordinary one — came back at 4 and hung off both ends of the
 *  screen. Callers whose zoom goes lower should say so; the default is kept
 *  because the architecture Editor's wheel really does stop at 4.
 */
export function fitBbox(
  bbox: Bbox,
  vw: number,
  vh: number,
  { padM = 3, maxK = 26, minK = K_MIN, insets = {}, plafon = K_MAX }:
    { padM?: number; maxK?: number; minK?: number; insets?: Insets;
      /** The hard ceiling, when the caller's own canvas has a different one.
       *  Defaults to K_MAX, so every existing call is unchanged. The tronson
       *  canvas goes to K_MAX_TRONSON (720 px/m) because it is a detail
       *  drawing — see `Panza2D` — and `Z` on a selected object has to be able
       *  to get that close; capped at 60 it would barely move. */
      plafon?: number } = {},
): View {
  const [x0, y0, x1, y1] = bbox;
  const w = Math.max(x1 - x0, 0.01) + padM * 2;
  const h = Math.max(y1 - y0, 0.01) + padM * 2;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const { l, t, w: aw, h: ah } = box(vw, vh, insets);
  // The CEILING is `plafon`, which defaults to K_MAX — so a fitted view is never
  // outside its own canvas's wheel clamp, and every caller that does not pass one
  // behaves exactly as before. Only the floor, and now the ceiling, are the
  // caller's business.
  const k = Math.min(Math.min(maxK, plafon), Math.max(minK, Math.min(aw / w, ah / h)));
  return { k, tx: l + aw / 2 - k * cx, ty: t + ah / 2 - k * cy };
}

export const clampK = (k: number, maxK = K_MAX) =>
  Math.min(Math.min(maxK, K_MAX), Math.max(K_MIN, k));

/** Where a metre point lands in viewport pixels under a view. */
export const toPx = (v: View, x: number, y: number): [number, number] =>
  [x * v.k + v.tx, y * v.k + v.ty];

/** How big a bbox renders, in pixels. */
export function bboxPxSize(v: View, bbox: Bbox): [number, number] {
  return [Math.abs(bbox[2] - bbox[0]) * v.k, Math.abs(bbox[3] - bbox[1]) * v.k];
}

/** Is the bbox inside the part of the viewport the panels do not cover?
 *
 *  Used to decide whether a jump is needed at all — re-framing when the element
 *  is already comfortably in sight throws away the context the user has built up. */
export function bboxVisible(
  v: View, bbox: Bbox, vw: number, vh: number,
  marginPx = 40, insets: Insets = {},
): boolean {
  const { l, t, w, h } = box(vw, vh, insets);
  const [x0, y0] = toPx(v, bbox[0], bbox[1]);
  const [x1, y1] = toPx(v, bbox[2], bbox[3]);
  return (
    Math.min(x0, x1) >= l + marginPx && Math.min(y0, y1) >= t + marginPx
    && Math.max(x0, x1) <= l + w - marginPx && Math.max(y0, y1) <= t + h - marginPx
  );
}

/** Whether clicking through to `bbox` should move the view.
 *
 *  Two reasons to move, and both matter: the element is off screen or under a
 *  panel, OR it is on screen but rendered too small to read. A 0,80 m stretch at
 *  13 px/m is ten pixels across — technically visible, and useless as an answer
 *  to "show me why this fails". */
export function shouldRefit(
  v: View, bbox: Bbox, vw: number, vh: number,
  insets: Insets = {}, minPx = 64,
): boolean {
  if (!bboxVisible(v, bbox, vw, vh, 40, insets)) return true;
  const [pw, ph] = bboxPxSize(v, bbox);
  return Math.min(pw, ph) < minPx;
}

/** Grow a bbox so a very small element still gets a sane frame. */
export function padBbox(bbox: Bbox, m: number): Bbox {
  return [bbox[0] - m, bbox[1] - m, bbox[2] + m, bbox[3] + m];
}

/** The bbox of a ring. */
export function ringBbox(ring: ReadonlyArray<ReadonlyArray<number>>): Bbox | null {
  if (!ring || !ring.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of ring) {
    if (p.length < 2) continue;
    x0 = Math.min(x0, p[0]); y0 = Math.min(y0, p[1]);
    x1 = Math.max(x1, p[0]); y1 = Math.max(y1, p[1]);
  }
  return Number.isFinite(x0) ? [x0, y0, x1, y1] : null;
}

/** The view after turning the drawing by `delta` degrees.
 *
 *  The transform is `translate(t) scale(k) rotate(rot, pivot)`, so the pivot is
 *  a FIXED point of the drawing — the centre of its frame. Turning about a fixed
 *  point alone is not what a person means by "rotate", though: with the drawing
 *  zoomed into one corner, a 15 degree turn about a pivot two hundred metres
 *  away swings that corner off the screen and there is nothing left to look at.
 *
 *  So the pivot stays fixed and the PAN is corrected: whatever sits under the
 *  middle of the viewport before the turn is still there after it. The drawing
 *  appears to turn in place, under the cursor, which is what a person means.
 *
 *  `vw`/`vh` are the viewport in pixels. Angles are degrees, clockwise, matching
 *  SVG's `rotate()` in a y-down space. */
export function rotateView(v: View, rot: number, delta: number,
                           pivot: { x: number; y: number },
                           vw: number, vh: number): View {
  const cx = vw / 2, cy = vh / 2;
  const a0 = (rot * Math.PI) / 180, a1 = ((rot + delta) * Math.PI) / 180;
  // Where the viewport centre lands in the drawing, relative to the pivot, with
  // the current turn undone.
  const ux = (cx - v.tx) / v.k - pivot.x, uy = (cy - v.ty) / v.k - pivot.y;
  const qx = ux * Math.cos(-a0) - uy * Math.sin(-a0);
  const qy = ux * Math.sin(-a0) + uy * Math.cos(-a0);
  // Where that same point would land under the new turn ...
  const wx = qx * Math.cos(a1) - qy * Math.sin(a1);
  const wy = qx * Math.sin(a1) + qy * Math.cos(a1);
  // ... and the pan that puts it back under the viewport centre.
  return { k: v.k, tx: cx - v.k * (wx + pivot.x), ty: cy - v.k * (wy + pivot.y) };
}

/** What a wheel event is asking for: PINCH and WHEEL zoom, a swipe moves.
 *
 *  One event type still carries three gestures, and the browser does not label
 *  them. But two of the three tells are conventions, not guesses:
 *
 *    * a PINCH arrives with `ctrlKey` set even though no key is down. That is
 *      what Chrome and Firefox do. Safari does NOT — it sends its own
 *      `gesturechange` instead, handled separately, so nothing here sees it.
 *    * a MOUSE WHEEL reports `wheelDeltaY` in whole multiples of 120. That
 *      multiple is the oldest convention on the web, one notch = 120, and a
 *      trackpad essentially never lands on it. Firefox has no `wheelDelta` at
 *      all, but reports `deltaMode = 1` — whole LINES — for a wheel and pixels
 *      for a trackpad, which says the same thing.
 *    * anything left over is a surface: two fingers, so move the view.
 *
 *  The earlier version of this asked only whether `deltaY` was a whole number,
 *  which a vertical trackpad swipe often is — that is the test that misfired
 *  about one time in ten and needed three buttons to argue with. Reading the
 *  120 step and the delta mode instead is what makes the wheel case safe to
 *  keep without them. `deltaX` is checked first regardless: nothing sideways
 *  ever comes out of a wheel. */
export function scrollIntent(
  e: { ctrlKey?: boolean; deltaX?: number; deltaY?: number;
       deltaMode?: number; wheelDeltaY?: number },
): "pan" | "zoom" {
  if (e.ctrlKey) return "zoom";
  if ((e.deltaX ?? 0) !== 0) return "pan";
  if (e.deltaMode === 1) return "zoom";
  const w = e.wheelDeltaY;
  if (typeof w === "number" && w !== 0 && Math.abs(w) % 120 === 0) return "zoom";
  return "pan";
}
